import React from 'react';
import MuiAvatar, { AvatarProps as MuiAvatarProps } from '@mui/material/Avatar';
import { BpDesignSystem } from '../../types';
import { styled } from '../../styles';
import { useLazyLoadingImage } from '../Image/useLazyLoadingImage';
import { getTokenColor, TokenColor } from '../../setup/theme/impl/colors';

export type AvatarSize = 'xs' | 's' | 'm' | 'l' | 'xl' | number;

export type AvatarProps = BpDesignSystem.PropsWithChildren<
  MuiAvatarProps & {
    size?: AvatarSize;
    backgroundColor?: string | TokenColor;
    /**
     *
     * lazy - loads images while in the viewport
     * eager - loading="lazy" browser strategy - its more aggressive strategy than ours "lazy"
     *         because of distance-from-viewport thresholds that can be even more than 2500px in some cases
     */
    loading?: 'eager' | 'lazy';
  }
>;

export const Avatar = ({
  dataTest,
  size,
  loading = 'lazy',
  style,
  slotProps,
  src,
  srcSet,
  children,
  backgroundColor,
  ...avatarProps
}: AvatarProps) => {
  const { ref, imgProps, finished, loadingInMemory, error } = useLazyLoadingImage({ src, srcSet }, loading);

  return (
    <StyledAvatar
      ref={ref}
      data-test={dataTest}
      data-image-loaded={src || srcSet ? finished : undefined}
      data-image-error={error}
      {...imgProps}
      style={{
        backgroundColor: backgroundColor ? getTokenColor(backgroundColor) : undefined,
        ...getAvatarSize(size),
        ...style
      }}
      $loadingImage={loadingInMemory}
      {...avatarProps}
      key={src}
    >
      {children}
    </StyledAvatar>
  );
};

interface SizeOutput {
  width: number;
  height: number;
  fontSize: number;
  lineHeight?: number;
}

export const getAvatarSize = (size: AvatarSize | undefined, { scale = 1 }: { scale?: number } = {}): SizeOutput => {
  if (typeof size === 'number') {
    return toSizeOutput(size * scale, true);
  }
  switch (size) {
    case 'xs':
      return toSizeOutput(24 * scale);
    case 's':
    case undefined:
      return toSizeOutput(40 * scale);
    case 'm':
      return toSizeOutput(55 * scale);
    case 'l':
      return toSizeOutput(80 * scale);
    case 'xl':
      return toSizeOutput(160 * scale);
  }
};

function toSizeOutput(size: number, wasNumber?: boolean): SizeOutput {
  return {
    width: size,
    height: size,
    fontSize: size / 2,
    lineHeight: wasNumber ? size : undefined
  };
}

const StyledAvatar = styled(MuiAvatar)<{ $loadingImage: boolean | undefined }>`
    transition: 0.2s background-color;
    
    &[data-image-loaded="true"] img {
        opacity: 1;
    }

    &[data-image-loaded="true"]:not([data-image-error="true"]) {
        background: none !important;
    }
`;
